import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import {
  useWorkOrderCourtesyLoans,
  type CourtesyLoan,
} from '@/hooks/useCourtesyVehicles';
import type { BookingStatus } from '@/types';
import { CourtesyLoanDialog, type CourtesyLoanDialogMode } from './CourtesyLoanDialog';
import { courtesyNoticeLoans, type CourtesyLoanNoticePlacement } from './courtesyNotice';

function loanVehicleLabel(loan: CourtesyLoan): string {
  return loan.displayName ? `${loan.registrationNumber} · ${loan.displayName}` : loan.registrationNumber;
}

interface CourtesyLoanNoticeProps {
  workOrderId: string;
  status: BookingStatus;
  placement: CourtesyLoanNoticePlacement;
}

/**
 * Visible courtesy-car reminder with the next action — hand over a reserved
 * car, record the return of one that is out (#4103). Uses the same loan
 * dialog as the courtesy-car card.
 */
export function CourtesyLoanNotice({ workOrderId, status, placement }: CourtesyLoanNoticeProps) {
  const { t } = useTranslation('workorders');
  const canUpdate = useAuthStore((s) => s.hasPermission('work_orders.update'));
  const { data: loans } = useWorkOrderCourtesyLoans(workOrderId);
  const [dialogLoan, setDialogLoan] = useState<CourtesyLoan | null>(null);
  const [dialogMode, setDialogMode] = useState<CourtesyLoanDialogMode>('view');

  const shown = courtesyNoticeLoans(loans ?? [], status, placement);
  if (shown.length === 0) return null;

  return (
    <>
      <Stack spacing={1} sx={{ mb: 1 }} data-testid={`courtesy-loan-notice-${placement}`}>
        {shown.map((loan) => {
          const isOut = loan.status === 'out';
          const mode: CourtesyLoanDialogMode = isOut ? 'return' : 'handover';
          return (
            <Alert
              key={loan.id}
              severity={isOut ? 'warning' : 'info'}
              action={
                canUpdate ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setDialogLoan(loan);
                      setDialogMode(mode);
                    }}
                  >
                    {isOut ? t('courtesy.return') : t('courtesy.handover')}
                  </Button>
                ) : undefined
              }
            >
              <AlertTitle>
                {t(isOut ? 'courtesy.noticeOut' : 'courtesy.noticeReserved', {
                  vehicle: loanVehicleLabel(loan),
                })}
              </AlertTitle>
              {t(isOut ? 'courtesy.noticeOutHint' : 'courtesy.noticeReservedHint')}
            </Alert>
          );
        })}
      </Stack>
      <CourtesyLoanDialog
        open={dialogLoan != null}
        loan={dialogLoan}
        mode={dialogMode}
        canUpdate={canUpdate}
        onClose={() => { setDialogLoan(null); }}
        onRequestMode={(next) => { setDialogMode(next); }}
      />
    </>
  );
}
